import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import RenameDatasetDialog from '~/components/Dialogs/RenameDatasetDialog';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';

interface ImageDeleteTarget {
    image: AvailableImage;
    affectedSets: StructureSet[];
}

export interface ImageDatasetActions {
    rename: (image: AvailableImage) => void;
    remove: (image: AvailableImage) => void;
    canRename: (image: AvailableImage) => boolean;
    canRemove: (image: AvailableImage) => boolean;
    dialogs: ReactNode;
}

export function useImageDatasetActions(): ImageDatasetActions {
    const intl = useIntl();
    const session = useSessionOrNull();
    const { deleteImage, deleteRawSeries, getAffectedStructureSets } =
        useDeleteDataset();
    const [deleteTarget, setDeleteTarget] = useState<ImageDeleteTarget | null>(
        null
    );
    const [renameTarget, setRenameTarget] = useState<Image3D | null>(null);
    // The series description is patient information: a rename open when it
    // is hidden closes without saving. The rows withhold the action itself.
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    useEffect(() => {
        if (!showSensitiveInfo) {
            setRenameTarget(null);
        }
    }, [showSensitiveInfo]);

    const canRename = useCallback(
        (image: AvailableImage) => !!image.imageObject && !!session,
        [session]
    );
    const canRemove = useCallback(
        (image: AvailableImage) => !!image.imageObject || !!image.rawSeries,
        []
    );

    const rename = useCallback((image: AvailableImage) => {
        if (image.imageObject) {
            setRenameTarget(image.imageObject);
        }
    }, []);

    const remove = useCallback(
        (image: AvailableImage) => {
            if (!image.imageObject && !image.rawSeries) {
                return;
            }
            setDeleteTarget({
                image,
                affectedSets: image.imageObject
                    ? getAffectedStructureSets(image.imageObject)
                    : [],
            });
        },
        [getAffectedStructureSets]
    );

    const confirmDelete = useCallback(() => {
        if (!deleteTarget) {
            return;
        }
        const { image } = deleteTarget;
        if (image.imageObject) {
            deleteImage(image.imageObject);
        } else if (image.rawSeries) {
            deleteRawSeries(image.rawSeries);
        }
        setDeleteTarget(null);
    }, [deleteTarget, deleteImage, deleteRawSeries]);

    const confirmRename = useCallback(
        (label: string) => {
            if (renameTarget && session) {
                session.imageService.rename({ id: renameTarget.id }, label);
            }
            setRenameTarget(null);
        },
        [renameTarget, session]
    );

    const deleteMessage = useMemo(() => {
        if (!deleteTarget) {
            return '';
        }
        const base = intl.formatMessage({ id: 'deleteImageConfirm' });
        if (deleteTarget.affectedSets.length === 0) {
            return base;
        }
        const names = deleteTarget.affectedSets
            .map((set) => set.dicomStructureSet.structureSetLabel)
            .join(', ');
        return `${base} ${intl.formatMessage(
            { id: 'deleteImageStructureSetWarning' },
            { structureSets: names }
        )}`;
    }, [deleteTarget, intl]);

    const dialogs = (
        <>
            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteImage' })}
                message={deleteMessage}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />
            {renameTarget && showSensitiveInfo && (
                <RenameDatasetDialog
                    open
                    title={intl.formatMessage({ id: 'renameImage' })}
                    initialValue={renameTarget.seriesMetadata.seriesDescription}
                    onCancel={() => setRenameTarget(null)}
                    onConfirm={confirmRename}
                />
            )}
        </>
    );

    return { rename, remove, canRename, canRemove, dialogs };
}
